import type {ReactElement} from 'react'
import {mapValues} from 'lodash'
import {useTranslation} from 'react-i18next'
import {exhaustive} from '@freckle/exhaustive'
import type {HTMLText} from '@freckle/educator-entities/ts/common/models/html-text'
import {
  toHTMLTextDangerous,
  innerHTMLText
} from '@freckle/educator-entities/ts/common/models/html-text'

type LangT = string
export type TranslateT = (key: string, options?: Record<string, unknown>) => string

// Returns a `t` function that more closely aligns with our usage
//
// This lets us avoid unsafely sending `HTMLText` to Locize without first unwrapping it.
// See `tHTMLText` for a `t` function that can translate with `HTMLText`
export function useSafeTranslation(ns?: string): {
  t: TranslateT
  i18n: {
    language: LangT
    changeLanguage: (lang: string) => void
    getFixedT: (lang: string) => TranslateT
  }
} {
  return useTranslation(ns)
}

/** Value of an interpolation parameter when using the 'translateHTML' function.
 * Strings will be escaped, HTMLText will not.
 */
export type TranslationValueHTML = HTMLText | string

/**
 * This can be useful when your translation template includes a mixture of both
 * HTML and non-HTML values. Values of type 'string' will be interpolated
 * as plain text (escaped), and values of type 'HTMLText' will be included as
 * HTML (unescaped).
 *
 * Any HTML in the translation template will be rendered as HTML (not escaped).
 */
export function translateHTML(
  t: TranslateT,
  i18nKey: string,
  values?: Record<string, TranslationValueHTML>
): HTMLText {
  return toHTMLTextDangerous(t(i18nKey, mapValues(values ?? {}, translationValueHTMLToString)))
}

/** Same as 'translateHTML' but wraps the result in a 'span' element. */
export function translateHTMLSpan(
  t: TranslateT,
  i18nKey: string,
  values?: Record<string, TranslationValueHTML>
): ReactElement {
  return <span {...innerHTMLText(translateHTML(t, i18nKey, values))} />
}

function escape(r: string): string {
  return r.replace(/[<>&]/g, r => `&#${r.charCodeAt(0)};`)
}

function translationValueHTMLToString(x: TranslationValueHTML): string {
  if (typeof x === 'string') {
    return escape(x)
  } else {
    switch (x.tag) {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      case 'html-text':
        return x.value
      default:
        return exhaustive(x.tag)
    }
  }
}
